import { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumbs,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material'
import { Bolt, Check, Hvac, Thermostat } from '@mui/icons-material'
import PageHeader from '../components/PageHeader'
import { AssetType } from '../enums/AssetType'
import { DeviceType } from '../enums'
import { usePolicy } from '../hooks'

export default function InstallPage() {
  /**
   * The translate function.
   */
  const { t } = useTranslation('installation')

  /**
   * The policy state.
   */
  const { isSuperadmin, isManufacturer } = usePolicy()

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The selected asset type.
   */
  const [selected, setSelected] = useState<AssetType | DeviceType>()

  /**
   * The query string.
   */
  const { search } = useLocation()

  /**
   * Check if the asset type is selected.
   */
  function isSelected(type: AssetType | DeviceType) {
    return selected === type
  }

  /**
   * Handle the "next" action.
   */
  function handleNext() {
    if (selected === AssetType.DEVICE) {
      navigate(`/install/device${search}`)
    } else if (selected === DeviceType.VALVE) {
      navigate(`/install/heater${search}`)
    } else if (selected === AssetType.RUUVI_TAG) {
      navigate(`/install/ruuvi-tag${search}`)
    }
  }

  /**
   * Handle the "cancel" action.
   */
  function handleCancel() {
    navigate('/')
  }

  return (
    <Container maxWidth={false}>
      <PageHeader
        title={t('installAsset')}
        breadcrumbs={
          <Breadcrumbs>
            <Link component={NavLink} to="/">
              {t('frontpage')}
            </Link>
            <Typography>{t('installation')}</Typography>
          </Breadcrumbs>
        }
      />

      <Container maxWidth="md">
        <Card>
          <CardHeader title={t('selectInstallableAsset')} />
          <CardContent sx={{ pt: 0 }}>
            <List>
              <ListItemButton
                selected={isSelected(AssetType.DEVICE)}
                color="success"
                onClick={() => setSelected(AssetType.DEVICE)}
              >
                <ListItemIcon sx={{ minWidth: 42 }}>
                  {isSelected(AssetType.DEVICE) ? <Check color="success" /> : <Bolt />}
                </ListItemIcon>
                <ListItemText
                  primary={t('asset:spineDevice')}
                  secondary={t('powerSupply')}
                  primaryTypographyProps={{
                    sx: { order: '0!important' }
                  }}
                  secondaryTypographyProps={{
                    sx: { order: '1!important' }
                  }}
                />
              </ListItemButton>

              {(isSuperadmin || isManufacturer) && (
                <ListItemButton
                  selected={isSelected(DeviceType.VALVE)}
                  onClick={() => setSelected(DeviceType.VALVE)}
                >
                  <ListItemIcon sx={{ minWidth: 42 }}>
                    {isSelected(DeviceType.VALVE) ? <Check color="success" /> : <Hvac />}
                  </ListItemIcon>
                  <ListItemText
                    primary={t('valve')}
                    secondary={t('heater')}
                    primaryTypographyProps={{
                      sx: { order: '0!important' }
                    }}
                    secondaryTypographyProps={{
                      sx: { order: '1!important' }
                    }}
                  />
                </ListItemButton>
              )}

              <ListItemButton
                selected={isSelected(AssetType.RUUVI_TAG)}
                color="primary"
                onClick={() => setSelected(AssetType.RUUVI_TAG)}
              >
                <ListItemIcon sx={{ minWidth: 42 }}>
                  {isSelected(AssetType.RUUVI_TAG) ? <Check color="success" /> : <Thermostat />}
                </ListItemIcon>
                <ListItemText
                  primary={t('ruuviTag')}
                  secondary={t('conditionMonitoring')}
                  primaryTypographyProps={{
                    sx: { order: '0!important' }
                  }}
                  secondaryTypographyProps={{
                    sx: { order: '1!important' }
                  }}
                />
              </ListItemButton>
            </List>
          </CardContent>

          <CardActions sx={{ justifyContent: 'flex-end' }}>
            <Button variant="text" color="inherit" onClick={handleCancel}>
              {t('cancel')}
            </Button>

            <Button
              variant="contained"
              color="primary"
              disabled={selected === undefined}
              onClick={handleNext}
            >
              {t('next')}
            </Button>
          </CardActions>
        </Card>
      </Container>
    </Container>
  )
}
